import React, { useState, useEffect, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./AdminDashboardPage.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_TRANSACTIONS_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function TransactionHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  const fetchTransactions = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BASE_TRANSACTIONS_URL}get-all-transactions?page=${page}&limit=${limit}`,
        {
          credentials: "include",
        }
      );
      const data = await response.json();
      if (response.ok) {
        setTransactions(data.transactions);
        setCurrentPage(data.pagination.page);
        setTotalPages(data.pagination.totalPages);
        setTotal(data.pagination.total);
        setLoading(false);
      } else {
        showToast(data.error || "Lỗi khi lấy dữ liệu giao dịch", "error");
        setLoading(false);
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleDelete = async (timestamp, phone) => {
    try {
      const response = await fetch(
        `${BASE_TRANSACTIONS_URL}delete-transaction?timestamp=${encodeURIComponent(timestamp)}&phone=${encodeURIComponent(phone)}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      const data = await response.json();
      if (response.ok) {
        showToast("Xóa giao dịch thành công", "success");
        await fetchTransactions(currentPage);
      } else {
        showToast(data.error || "Lỗi khi xóa giao dịch", "error");
        return false; // Keep modal open on error
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
      return false; // Keep modal open on error
    }
  };

  const confirmDelete = (timestamp, phone) => {
    showModal({
      title: "Xác Nhận Xóa",
      message: `Bạn có chắc chắn muốn xóa giao dịch lúc ${timestamp} cho số điện thoại ${phone}?`,
      onConfirm: () => handleDelete(timestamp, phone),
      onCancel: null, // Uses default hideModal from ModalContext
    });
  };

  const [pageInput, setPageInput] = useState("");

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      setPageInput("");
    }
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handlePageInputSubmit = (e) => {
    e.preventDefault();
    const page = parseInt(pageInput);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setPageInput("");
    } else {
      showToast(`Vui lòng nhập số trang từ 1 đến ${totalPages}`, "error");
    }
  };

  const SkeletonRow = () => (
    <tr className={cx("tr")}>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-button")}></div>
      </td>
    </tr>
  );

  return (
    <div className={cx("transactions-section")}>
      {loading ? (
        <>
          <div className={cx("pagination-info")}>
            <div className={cx("skeleton", "skeleton-text", "skeleton-info")}></div>
          </div>
          <table className={cx("table")}>
            <thead>
              <tr>
                <th className={cx("th")}>Thời Gian</th>
                <th className={cx("th")}>Số Điện Thoại</th>
                <th className={cx("th")}>Điểm</th>
                <th className={cx("th")}>Hành Động</th>
              </tr>
            </thead>
            <tbody>
              {[...Array(10)].map((_, index) => (
                <SkeletonRow key={index} />
              ))}
            </tbody>
          </table>
        </>
      ) : transactions.length === 0 ? (
        <p className={cx("no-data")}>Không có giao dịch nào</p>
      ) : (
        <>
          <div className={cx("pagination-info")}>
            Hiển thị {((currentPage - 1) * limit) + 1} - {Math.min(currentPage * limit, total)} trong tổng số {total} giao dịch
          </div>
          <table className={cx("table")}>
            <thead>
              <tr>
                <th className={cx("th")}>Thời Gian</th>
                <th className={cx("th")}>Số Điện Thoại</th>
                <th className={cx("th")}>Điểm</th>
                <th className={cx("th")}>Hành Động</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction, index) => (
                <tr key={index} className={cx("tr")}>
                  <td className={cx("td")}>{transaction.timestamp}</td>
                  <td className={cx("td")}>{transaction.phone}</td>
                  <td
                    className={cx("td", {
                      positive: transaction.points > 0,
                      negative: transaction.points < 0,
                    })}
                  >
                    {transaction.points}
                  </td>
                  <td className={cx("td")}>
                    <div className={cx("button-container")}>
                      <button
                        className={cx("delete-button")}
                        onClick={() =>
                          confirmDelete(transaction.timestamp, transaction.phone)
                        }
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className={cx("pagination")}>
              <button
                className={cx("pagination-button")}
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Trước
              </button>
              <span className={cx("pagination-info-text")}>
                Trang {currentPage} / {totalPages}
              </span>
              <form onSubmit={handlePageInputSubmit} className={cx("pagination-input-form")}>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={pageInput}
                  onChange={handlePageInputChange}
                  placeholder="Trang"
                  className={cx("pagination-input")}
                />
                <button type="submit" className={cx("pagination-go-button")}>
                  Đi
                </button>
              </form>
              <button
                className={cx("pagination-button")}
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Sau
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default TransactionHistory;
